import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Message, UseFormRegisterReturn, UseFormReturn, ValidateResult } from 'react-hook-form';
import { idGenerator } from '../../../helpers/idGenerator';
import classNames from 'classnames';
import classes from './SelectAllCheckboxes.module.scss';
import checkBoxClasses from '../../forms/Checkbox/Checkbox.module.scss';
import inputClasses from '../../forms/form.module.scss';
import { CheckboxViewProps } from '../../forms/Checkbox/Checkbox.view';
import { CheckboxStateType } from './SelectAllCheckboxes.controller';
import { ValidationRule } from 'react-hook-form/dist/types/validator';

export type SelectAllValidationKeys = 'required' | 'all' | 'none';

export type SelectAllValidationType = {
  [key in SelectAllValidationKeys]?: Message | ValidationRule<boolean>;
};

export type SelectAllCheckboxesViewProps = {
  checkboxState: CheckboxStateType;
  disabled?: boolean;
  formFieldName: string | string[];
  formFieldNamePrincipal?: string;
  formInstance: UseFormReturn;
  id?: string;
  label: string;
  name?: string;
  onSetState: (value: boolean) => void;
  rules?: SelectAllValidationType;
  size: CheckboxViewProps['size'];
  wrapper?: CheckboxViewProps['wrapper'];
  wrapperClickable?: boolean;
  stopPropagation?: boolean;
};

export const SelectAllCheckboxesView = (props: SelectAllCheckboxesViewProps): JSX.Element => {
  const {
    formInstance,
    disabled,
    label,
    wrapper,
    size,
    id,
    checkboxState,
    formFieldName,
    onSetState,
    rules,
    wrapperClickable
  } = props;
  const inputID = useMemo(() => id ?? idGenerator(label), [id, label]);
  const localRef = useRef<HTMLInputElement | null>(null);
  const formInstanceRef = useRef<UseFormReturn>(formInstance);

  const globalClasses = classNames(inputClasses.FormInputs, inputClasses[size]);
  const elemWrapperClasses = classNames(
    checkBoxClasses.CheckboxWrapper,
    checkBoxClasses[`CheckboxSize_${size}`],
    wrapperClickable && classes.WrapperHover
  );

  const name = props.name || 'select_all_checkboxes_toggle';

  const [mounted, setMounted] = useState(false);
  const shouldValidateRef = useRef<boolean>(false);

  const registerUseForm = useCallback((): UseFormRegisterReturn => {
    if (rules) {
      const key = Object.keys(rules)[0] as SelectAllValidationKeys;
      const message = rules[key] as string;
      return formInstanceRef.current.register(name, {
        validate: (value: boolean): ValidateResult => {
          switch (key) {
            case 'required':
              return localRef.current?.indeterminate || localRef.current?.checked ? undefined : message;
            case 'all':
              if (value) {
                return undefined;
              }
              return localRef.current?.checked ? undefined : message;
            case 'none':
              return localRef.current?.checked || localRef.current?.indeterminate ? message : undefined;
          }
          return false;
        }
      });
    } else {
      return formInstanceRef.current.register(name);
    }
  }, [name, rules]);

  const register = registerUseForm();

  useEffect(() => {
    if (localRef.current) {
      (localRef.current as HTMLInputElement).indeterminate = checkboxState === 'indeterminate';
      (localRef.current as HTMLInputElement).checked = checkboxState === true;
      const fields = formFieldName
        ? typeof formFieldName === 'string'
          ? [name, formFieldName]
          : [name, ...formFieldName]
        : [name];
      shouldValidateRef.current && formInstanceRef.current.trigger(fields);
    }
  }, [name, checkboxState, formFieldName]);

  // We need this use effect to prevent showing error message on first render, since we use custom trigger to do the validation
  useEffect(() => {
    if (mounted) {
      shouldValidateRef.current = true;
    } else {
      setMounted(true);
    }
  }, [mounted]);

  let elem = (
    <div
      className={elemWrapperClasses}
      onClick={e => {
        if (wrapperClickable) {
          const checkboxRef = localRef.current as HTMLInputElement;
          const target = e.target;
          const currentTarget = e.currentTarget;
          if (target === currentTarget) {
            // If the click is on the wrapper itself, and not the inner checkbox/label.
            checkboxRef.click();
          }
        }
        props.stopPropagation && e.stopPropagation();
      }}>
      <input
        id={inputID}
        type={'checkbox'}
        name={name}
        data-indeterminate={checkboxState === 'indeterminate'}
        onChange={e =>
          register
            .onChange(e)
            .then(() => {
              onSetState(e.target.checked);
            })
            .catch(() => {
              // prevents runtime error
            })
        }
        onBlur={register.onBlur}
        className={globalClasses}
        disabled={disabled}
        ref={ref => {
          localRef.current = ref;
          register.ref(localRef.current);
        }}
      />
      <label htmlFor={inputID}>{label}</label>
    </div>
  );

  if (wrapper) {
    elem = <div className={'input-wrapper'}>{elem}</div>;
  }

  return elem;
};
